import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { AutoSizer, Table, Column } from 'react-virtualized';
import { getFormTypeColour, formatTime, formatDate, resolveImageSrc } from '../utils/occurrenceHelpers';

function DetailTable({ incidents, onViewIncident, hasAdvancedOccurrences }) {
    const [lightboxSrc, setLightboxSrc] = useState(null);
    const uniqueSites = useMemo(() => new Set(incidents.map(i => i.siteName).filter(Boolean)).size, [incidents]);
    const uniqueStaff = useMemo(() => new Set(incidents.map(i => i.staffName).filter(Boolean)).size, [incidents]);
    const photoCount = useMemo(() => incidents.filter(i => i.photoUrl || i.photoFile).length, [incidents]);

    const rowGetter = ({ index }) => incidents[index];

    const rowClassName = ({ index }) => {
        if (index < 0) return 'occurrence-table__header-row';
        return index % 2 === 0 ? 'occurrence-table__row--even' : 'occurrence-table__row--odd';
    };

    const statusDotRenderer = ({ rowData }) => (
        <div
            className="occurrence-table__status-dot"
            style={{ backgroundColor: getFormTypeColour(rowData.formTitle) }}
            title={rowData.formTitle}
        />
    );

    const dateRenderer = ({ rowData }) => formatDate(rowData.dateOfIncident);
    const timeRenderer = (field) => ({ rowData }) => formatTime(rowData[field]);

    const formRenderer = ({ rowData }) => (
        <span className="occurrence-detail__form-badge">
            <span
                className="occurrence-table__status-dot occurrence-table__status-dot--inline"
                style={{ backgroundColor: getFormTypeColour(rowData.formTitle) }}
            />
            {rowData.formTitle}
        </span>
    );

    const notesRenderer = ({ cellData }) => (
        <span className="occurrence-table__cell--truncated" title={cellData}>
            {cellData && cellData.length > 60 ? `${cellData.substring(0, 60)}...` : cellData}
        </span>
    );

    const locationRenderer = ({ rowData }) => {
        if (!rowData.lat || !rowData.lng) return '';
        return (
            <a
                className="occurrence-table__location-link"
                href={`https://www.google.com/maps?q=${rowData.lat},${rowData.lng}`}
                target="_blank"
                rel="noopener noreferrer"
            >
                View
            </a>
        );
    };

    const photoRenderer = ({ rowData }) => {
        const src = resolveImageSrc(rowData.photoUrl) || resolveImageSrc(rowData.photoFile);
        if (!src) return '';
        return (
            <img
                className="occurrence-table__photo-thumb"
                src={src}
                alt="Occurrence"
                loading="lazy"
                onClick={(e) => { e.stopPropagation(); setLightboxSrc(src); }}
                style={{ cursor: 'pointer' }}
            />
        );
    };

    const actionLabel = hasAdvancedOccurrences ? 'View / Follow-Up' : 'View';

    const actionsRenderer = ({ rowData }) => (
        <button
            className="occurrence-table__view-btn"
            onClick={() => onViewIncident?.(rowData)}
        >
            {actionLabel}
        </button>
    );

    return (
        <div className="occurrence-detail">
            <div className="occurrence-overview__chips">
                <span className="occurrence-overview__chip">
                    <strong>{incidents.length}</strong> Occurrences
                </span>
                <span className="occurrence-overview__chip">
                    <strong>{uniqueSites}</strong> Sites
                </span>
                <span className="occurrence-overview__chip">
                    <strong>{uniqueStaff}</strong> Staff
                </span>
                <span className="occurrence-overview__chip">
                    <strong>{photoCount}</strong> Photos
                </span>
            </div>

            <div className="occurrence-detail__table">
                <AutoSizer>
                    {({ width, height }) => (
                        <Table
                            width={width}
                            height={height}
                            headerHeight={36}
                            rowHeight={42}
                            rowCount={incidents.length}
                            rowGetter={rowGetter}
                            rowClassName={rowClassName}
                        >
                            <Column
                                label=""
                                dataKey="formTitle"
                                width={30}
                                cellRenderer={statusDotRenderer}
                            />
                            <Column
                                label="Date"
                                dataKey="dateOfIncident"
                                width={90}
                                cellRenderer={dateRenderer}
                            />
                            <Column label="Site" dataKey="siteName" style={{ whiteSpace: 'nowrap', textWrap: 'wrap' }} width={150} />
                            <Column label="Department" dataKey="department" style={{ whiteSpace: 'nowrap', textWrap: 'wrap' }} width={120} />
                            <Column label="Staff" dataKey="staffName" style={{ whiteSpace: 'nowrap', textWrap: 'wrap' }} width={130} />
                            <Column label="Role" dataKey="roleName" style={{ whiteSpace: 'nowrap', textWrap: 'wrap' }} width={110} />
                            <Column
                                label="Shift Start"
                                dataKey="shiftStart"
                                width={80}
                                cellRenderer={timeRenderer('shiftStart')}
                            />
                            <Column
                                label="Shift Finish"
                                dataKey="shiftFinish"
                                width={80}
                                cellRenderer={timeRenderer('shiftFinish')}
                            />
                            <Column
                                label="Form Type"
                                dataKey="formTitle"
                                width={140}
                                cellRenderer={formRenderer}
                            />
                            <Column
                                label="Notes"
                                dataKey="notes"
                                style={{ whiteSpace: 'nowrap', textWrap: 'wrap' }}
                                width={400}
                                cellRenderer={notesRenderer}
                            />
                            <Column
                                label="Location"
                                dataKey="lat"
                                width={65}
                                cellRenderer={locationRenderer}
                            />
                            <Column
                                label="Photo"
                                dataKey="photoUrl"
                                width={135}
                                cellRenderer={photoRenderer}
                            />
                            <Column
                                label="Actions"
                                dataKey="incidentID"
                                width={120}
                                cellRenderer={actionsRenderer}
                            />
                        </Table>
                    )}
                </AutoSizer>
            </div>

            {lightboxSrc && (
                <div className="occurrence-lightbox" onClick={() => setLightboxSrc(null)}>
                    <img
                        src={lightboxSrc}
                        alt="Occurrence"
                        className="occurrence-lightbox__image"
                        onClick={(e) => e.stopPropagation()}
                    />
                    <button className="occurrence-lightbox__close" onClick={() => setLightboxSrc(null)}>✕</button>
                </div>
            )}
        </div>
    );
}

DetailTable.propTypes = {
    incidents: PropTypes.array.isRequired,
    onViewIncident: PropTypes.func,
    hasAdvancedOccurrences: PropTypes.bool,
};

export default DetailTable;
